import { ClientAreaDomain } from '@/api/ClientArea/ClientAreaDomain'
import {
  CreateUserDashboardLayoutRequest,
  UserDashboardLayoutDto,
  UserWidgetDto,
} from '@/api/ClientArea/Dashboards'
import { TickmillCompanyTypes } from '@/api/Clients/Types/TickmillCompanyTypes'
import { ClientAreaGroup } from 'api/ClientArea/ClientAreaGroup'
import { ClientAreaLog } from 'api/ClientArea/ClientAreaLog'
import { CreateLandingPage } from 'api/ClientArea/CreateLandingPage'
import {
  CreatePromoMaterials,
  CreatePromoMaterialsForLandingPage,
} from 'api/ClientArea/CreatePromoMaterials'
import {
  CreateReferralMaterial,
  CreateReferralMaterialCategory,
} from 'api/ClientArea/CreateReferralMaterial'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import {
  PreviewPromoMaterials,
  PreviewPromoMaterialsLandingPage,
} from 'api/ClientArea/PreviewPromoMaterials'
import { PromoMaterialDto, PromoMaterialLandingPageDto } from 'api/ClientArea/PromoMaterialDto'
import { PromoMaterialsOptionsDto } from 'api/ClientArea/PromoMaterialsOptionsDto'
import {
  ReferralMaterialsCategoriesDto,
  ReferralMaterialsDto,
  UpdateReferralMaterialCategoryDto,
  UpdateReferralMaterialDto,
} from 'api/ClientArea/ReferralMaterialsDto'
import { UpdateLandingPageDto } from 'api/ClientArea/UpdateLandingPageDto'
import {
  UpdatePromoMaterial,
  UpdatePromoMaterialLandingPage,
} from 'api/ClientArea/UpdatePromoMaterial'
import { AuditLog } from 'api/Clients/Types/AuditLog'
import { POIDocumentType } from 'api/Clients/Types/DocumentType'
import {
  ChangeNotificationDto,
  NotificationDto,
  ReplaceNotificationImageDto,
  UploadNotificationImageDto,
} from 'api/Clients/Types/NotificationDto'
import { NameDto } from 'api/Common/Types'
import { DocumentRequestType } from 'api/Documents/Types/DocumentRequests'
import { PageData } from 'api/Pagination/Types'
import {
  Email,
  EmailPreview,
  EmailPreviewRequest,
  SendEmail,
} from 'api/Trading-Accounts/Types/EmailsDetailsDto'
import { AudienceTypeDto } from 'routes/Marketing/Notifications/NotificationDetailsForm'
import { CreateFeatureFlag } from 'routes/Product/FeatureFlags/Details/FeatureFlagDetailsPage'
import { FeatureFlag } from 'routes/Product/FeatureFlags/FeatureFlags'
import {
  CreatePlatformVersion,
  PlatformVersion,
} from 'routes/Product/PlatformVersions/PlatformVersions'
import {
  DomainName,
  DomainNameParams,
  LandingPageParams,
  PromoPageFetch,
  SGLLInk,
  SGLLinkParams,
} from 'types/Marketing/SGLLinkGenerator.types'

import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'
import { createLanguageQueryString } from './queryStringUtils'

export class ClientAreaApiClient {
  constructor(private apiClient: ApiClient) {}

  getClientAreaGroups: () => Promise<ClientAreaGroup[]> = () => {
    return this.apiClient.get(`clientarea/groups`)
  }
  getClientAreaLogs: (query?: PageQuery) => Promise<PageData<ClientAreaLog[]>> = (query) => {
    return this.apiClient.get(`/clientarea/actions?${preparePageQueryParams(query)}`)
  }
  sentDocumentStatusNotification: (clientId: string, documentId: string) => Promise<void> = (
    clientId,
    documentId
  ) => {
    return this.apiClient.post(
      `/clientarea/clients/${clientId}/documents/${documentId}/status-notification`,
      {}
    )
  }
  sendDocumentPasswordRequest: (clientId: string, documentId: string) => Promise<void> = (
    clientId,
    documentId
  ) => {
    return this.apiClient.post(
      `/clientarea/clients/${clientId}/documents/${documentId}/password-request`,
      {}
    )
  }
  postClassificationClientTestEmail: (clientId: string, templateId: string) => Promise<void> = (
    clientId,
    templateId
  ) => {
    return this.apiClient.post(
      `clientarea/clients/${clientId}/requests/ep-classification-additional-info/${templateId}`,
      {}
    )
  }
  sendOtherRequest: ({
    clientId,
    requestType,
  }: {
    clientId: string
    requestType: DocumentRequestType
  }) => Promise<void> = ({ clientId, requestType }) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/other/${requestType}`, {})
  }
  updateClientPOIRequest: (clientId: string, type: POIDocumentType) => Promise<void> = (
    clientId,
    type
  ) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/${type}`, {})
  }
  sendBankStatementNeededCCCoveredIBRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(
      `clientarea/clients/${clientId}/requests/transaction-documents/cc-covered-ib`,
      {}
    )
  }
  sendBankStatementNeededDepositConfirmationRequest: (clientId: string) => Promise<void> = (
    clientId
  ) => {
    return this.apiClient.post(
      `clientarea/clients/${clientId}/requests/transaction-documents/deposit-confirmation`,
      {}
    )
  }
  getLandingPages: ({ params }: { params?: LandingPageParams }) => Promise<PromoPageFetch> = ({
    params,
  }) => {
    return this.apiClient.get(
      `clientarea/promo-materials/landing-pages?Search=(TickmillCompanyId == ${params?.companyId}) AND (LanguageId == ${params?.language}) AND (Enabled == true) AND (Expired == false) &pageSize=100`
    )
  }
  getDomainNames: (params?: DomainNameParams) => Promise<DomainName[]> = (params) => {
    return this.apiClient.get(
      `clientarea/promo-materials/public-domains?TickmillCompanyId=${params?.companyId}`
    )
  }

  getSglLink: (params?: SGLLinkParams) => Promise<SGLLInk> = (params) => {
    return this.apiClient.get(
      `clientarea/promo-materials/sgl-link${createLanguageQueryString(params?.language)}&landingPageId=${params?.landingPageId}&domain=${params?.domain}&platform=${params?.platform}&pageSize=100`
    )
  }
  getIBLandingPages: (query?: PageQuery) => Promise<PageData<LandingPageDto[]>> = (query) => {
    return this.apiClient.get(
      `clientarea/promo-materials/landing-pages?${preparePageQueryParams(query)}`
    )
  }
  createLandingPage: (data?: CreateLandingPage) => Promise<void> = (data) => {
    return this.apiClient.post(`clientarea/promo-materials/landing-pages`, data)
  }
  getLandingPageById: (landingId: string) => Promise<LandingPageDto> = (landingPageId) => {
    return this.apiClient.get(`clientarea/promo-materials/landing-pages/${landingPageId}`)
  }
  updateLandingPage: (landingPageId: string, data: UpdateLandingPageDto) => Promise<void> = (
    landingPageId,
    data
  ) => {
    return this.apiClient.put(`clientarea/promo-materials/landing-pages/${landingPageId}`, data)
  }
  deleteLandingPage: (landingId: string) => Promise<void> = (landingPageId: string) => {
    return this.apiClient.delete(`clientarea/promo-materials/landing-pages/${landingPageId}`)
  }
  getLandingPagesEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`clientarea/promo-materials/landing-pages/${id}/events`)
  }
  createPromoMaterialsForLandingPage: (data?: CreatePromoMaterialsForLandingPage) => Promise<void> =
    (data) => {
      return this.apiClient.post(`clientarea/promo-materials/promo-materials`, data)
    }
  validatePromoMaterialsForLandingPage: (
    data?: CreatePromoMaterialsForLandingPage
  ) => Promise<PreviewPromoMaterialsLandingPage> = (data) => {
    return this.apiClient.post(`clientarea/promo-materials/promo-materials/preview`, data)
  }
  getPromoMaterialsByPromoMaterialIdForLandingPage: (
    promoMaterialId: string
  ) => Promise<PromoMaterialLandingPageDto> = (promoMaterialId) => {
    return this.apiClient.get(`clientarea/promo-materials/promo-materials/${promoMaterialId}`)
  }
  updatePromoMaterialsByPromoMaterialIdForLandingPage: (
    promoMaterialId: string,
    body: UpdatePromoMaterialLandingPage
  ) => Promise<void> = (promoMaterialId, body) => {
    return this.apiClient.put(`clientarea/promo-materials/promo-materials/${promoMaterialId}`, body)
  }
  updatePromoMaterialsByPromoMaterialId: (
    referralMaterialId: string,
    promoMaterialId: string,
    body: UpdatePromoMaterial
  ) => Promise<void> = (referralMaterialId, promoMaterialId, body) => {
    return this.apiClient.put(
      `clientarea/referral-materials/${referralMaterialId}/promo-assets/${promoMaterialId}`,
      body
    )
  }
  getPromoMaterialsByLandingPageId: (
    landingPageId: string,
    query?: PageQuery
  ) => Promise<PageData<PromoMaterialLandingPageDto>> = (landingPageId, query) => {
    return this.apiClient.get(
      `clientarea/promo-materials/landing-pages/${landingPageId}/promo-materials?${preparePageQueryParams(query)}`
    )
  }
  deletePromoMaterialsByPromoMaterialIdForLandingPage: (promoMaterialId: string) => Promise<void> =
    (promoMaterialId) => {
      return this.apiClient.delete(`clientarea/promo-materials/promo-materials/${promoMaterialId}`)
    }
  getPromoMaterialsOptions: () => Promise<PromoMaterialsOptionsDto> = () => {
    return this.apiClient.get(`clientarea/promo-materials/options`)
  }
  getPromoMaterialEventsForLandingPage: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`clientarea/promo-materials/${id}/events`)
  }
  getBaseURLTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clientarea/clientarea/base-url-types`)
  }
  getNotifications: (query?: PageQuery) => Promise<PageData<NotificationDto[]>> = (query) => {
    return this.apiClient.get(`clientarea/notifications?${preparePageQueryParams(query)}`)
  }
  getNotificationById: (id: string, domain: string) => Promise<NotificationDto> = (id, domain) => {
    return this.apiClient.get(`clientarea/notifications/${id}?Domain=${domain}`)
  }
  updateNotification: (id: string, data: ChangeNotificationDto) => Promise<NotificationDto> = (
    id,
    data
  ) => {
    return this.apiClient.put(`clientarea/notifications/${id}`, data)
  }
  createNotification: (data: ChangeNotificationDto) => Promise<NotificationDto> = (data) => {
    return this.apiClient.post(`clientarea/notifications`, data)
  }
  createRequestTranslationRequest: () => Promise<void> = () => {
    return this.apiClient.post(`clientarea/translations/entities`, {})
  }
  getNotificationAudienceTypes: () => Promise<AudienceTypeDto[]> = () => {
    return this.apiClient.get(`clientarea/notifications/notification-audience-types`)
  }
  getNotificationTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clientarea/notifications/notification-types`)
  }
  getNotificationBaseUrlTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clientarea/clientarea/base-url-types`)
  }
  getNotificationPlatforms: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clientarea/notifications/platforms`)
  }
  getNotificationProductStatuses: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clientarea/notifications/product-statuses`)
  }
  uploadNotificationImage: (id: string, data: UploadNotificationImageDto) => Promise<void> = (
    id,
    data
  ) => {
    return this.apiClient.post(`clientarea/notifications/${id}/add-image`, data)
  }
  getNotificationsEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`clientarea/notifications/${id}/events`)
  }
  getDomains: (entityId?: TickmillCompanyTypes) => Promise<ClientAreaDomain> = (
    entityId = TickmillCompanyTypes.Tickmill_SC_Company
  ) => {
    return this.apiClient.get(`notifications/domains/by-tickmill-company/${entityId}`)
  }
  replaceNotificationImage: (
    id: string,
    imageId: string,
    data: ReplaceNotificationImageDto
  ) => Promise<void> = (id, imageId, data) => {
    return this.apiClient.put(`clientarea/notifications/${id}/update-image/${imageId}`, data)
  }
  getEmails: (query?: PageQuery) => Promise<PageData<Email[]>> = (query) => {
    return this.apiClient.get(`/clientarea/email-templates?${preparePageQueryParams(query)}`)
  }
  getEmailFromTypes: () => Promise<NameDto<string>[]> = () => {
    return this.apiClient.get(`/clientarea/email-templates/sender-types`)
  }
  getEmailGroupTypes: () => Promise<NameDto<string>[]> = () => {
    return this.apiClient.get(`/clientarea/email-templates/group-types`)
  }
  getEmailPreview: (data: EmailPreviewRequest) => Promise<EmailPreview> = (data) => {
    return this.apiClient.post(`/clientarea/email-template-preview`, data)
  }
  sendEmail: (data: SendEmail) => Promise<void> = (data) => {
    return this.apiClient.post(`/clientarea/send-email`, data)
  }
  getVersions: (query?: PageQuery) => Promise<PageData<NameDto<string>[]>> = (query) => {
    return this.apiClient.get(`clientarea/management/versions?${preparePageQueryParams(query)}`)
  }
  addVersion: (data: { name: string }) => Promise<PageData<void>> = (data) => {
    return this.apiClient.post(`clientarea/management/versions`, data)
  }
  updateVersion: (data: NameDto<string>) => Promise<PageData<void>> = (data) => {
    return this.apiClient.put(`clientarea/management/versions/${data.id}`, { name: data.name })
  }
  getFeatureFlags: (query?: PageQuery) => Promise<PageData<FeatureFlag[]>> = (query) => {
    return this.apiClient.get(
      `clientarea/management/feature-flags?${preparePageQueryParams(query)}`
    )
  }
  getFeatureFlagById: (id: string) => Promise<FeatureFlag> = (id) => {
    return this.apiClient.get(`clientarea/management/feature-flags/${id}`)
  }
  addFeatureFlag: (data: CreateFeatureFlag) => Promise<void> = (data) => {
    return this.apiClient.post(`clientarea/management/feature-flags`, data)
  }
  updateFeatureFlag: (data: FeatureFlag) => Promise<void> = (data) => {
    return this.apiClient.put(`clientarea/management/feature-flags/${data.id}`, data)
  }
  deleteFeatureFlag: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`clientarea/management/feature-flags/${id}`)
  }
  getPlatformVersions: (query?: PageQuery) => Promise<PageData<PlatformVersion[]>> = (query) => {
    return this.apiClient.get(
      `clientarea/management/platform-versions?${preparePageQueryParams(query)}`
    )
  }
  addPlatformVersion: (data: CreatePlatformVersion) => Promise<PageData<void>> = (data) => {
    return this.apiClient.post(`clientarea/management/platform-versions`, data)
  }
  updatePlatformVersion: (data: PlatformVersion) => Promise<PageData<void>> = (data) => {
    return this.apiClient.put(`clientarea/management/platform-versions/${data.id}`, data)
  }
  getReferralMaterials: (query?: PageQuery) => Promise<PageData<ReferralMaterialsDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`clientarea/referral-materials?${preparePageQueryParams(query)}`)
  }
  getReferralMaterialsById: (referralMaterialId: string) => Promise<ReferralMaterialsDto> = (
    referralMaterialId
  ) => {
    return this.apiClient.get(`clientarea/referral-materials/${referralMaterialId}`)
  }
  getReferralMaterialsCategorySettingsById: (
    referralMaterialCategoryId: string
  ) => Promise<ReferralMaterialsCategoriesDto> = (referralMaterialCategoryId) => {
    return this.apiClient.get(
      `clientarea/referral-materials/categories/${referralMaterialCategoryId}`
    )
  }
  getReferralMaterialEvents: (referralMaterialId: string) => Promise<AuditLog[]> = (
    referralMaterialId
  ) => {
    return this.apiClient.get(`clientarea/referral-materials/${referralMaterialId}/events`)
  }
  getReferralMaterialsCategoryEvents: (referralMaterialsCategoryId: string) => Promise<AuditLog[]> =
    (referralMaterialsCategoryId) => {
      return this.apiClient.get(
        `/clientarea/referral-materials/categories/${referralMaterialsCategoryId}/events`
      )
    }
  getPromoMaterialEvents: (
    referralMaterialId: string,
    promoAssetId: string
  ) => Promise<AuditLog[]> = (referralMaterialId, promoAssetId) => {
    return this.apiClient.get(
      `clientarea/referral-materials/${referralMaterialId}/promo-assets/${promoAssetId}/events`
    )
  }
  getReferralMaterialsPromoAssetsById: (
    referralMaterialId: string
  ) => Promise<ReferralMaterialsDto> = (referralMaterialId) => {
    return this.apiClient.get(`clientarea/referral-materials/${referralMaterialId}/promo-assets`)
  }
  getPromoMaterialsByPromoMaterialId: (
    referralMaterialId: string,
    promoMaterialId: string
  ) => Promise<PromoMaterialDto> = (referralMaterialId, promoMaterialId) => {
    return this.apiClient.get(
      `clientarea/referral-materials/${referralMaterialId}/promo-assets/${promoMaterialId}`
    )
  }
  getPromoMaterialsByReferralMaterialsId: (
    referralMaterialId: string,
    query?: PageQuery
  ) => Promise<PageData<PromoMaterialDto>> = (referralMaterialId, query) => {
    return this.apiClient.get(
      `clientarea/referral-materials/${referralMaterialId}/promo-assets?${preparePageQueryParams(query)}`
    )
  }
  getReferralMaterialsTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('clientarea/referral-materials/types')
  }
  getReferralMaterialsAllowedSizes: (referralMaterialTypeId: number) => Promise<NameDto[]> = (
    referralMaterialTypeId
  ) => {
    return this.apiClient.get(
      `clientarea/referral-materials/types/${referralMaterialTypeId}/allowed-sizes`
    )
  }
  getReferralMaterialsPromoContentTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('clientarea/referral-materials/promo-assets/content-types')
  }
  getReferralMaterialsPromoSizes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('clientarea/referral-materials/promo-assets/sizes')
  }
  getReferralMaterialsCategories: (
    query?: PageQuery
  ) => Promise<PageData<ReferralMaterialsCategoriesDto[]>> = (query) => {
    return this.apiClient.get(
      `clientarea/referral-materials/categories?${preparePageQueryParams(query)}`
    )
  }
  getReferralMaterialsCategoriesByEntity: (
    tickmillCompanyId: number | string,
    isActive?: boolean
  ) => Promise<(NameDto<string> & { isActive: boolean })[]> = (tickmillCompanyId, isActive) => {
    return this.apiClient.get(
      `clientarea/referral-materials/categories/basic?tickmillCompanyId=${tickmillCompanyId}${isActive ? '&isActive=true' : ''}`
    )
  }
  createReferralMaterial: (data?: CreateReferralMaterial) => Promise<void> = (data) => {
    return this.apiClient.post('clientarea/referral-materials', data)
  }
  createReferralMaterialsCategory: (data: CreateReferralMaterialCategory) => Promise<void> = (
    data
  ) => {
    return this.apiClient.post(`clientarea/referral-materials/categories`, data)
  }
  createPromoMaterialsForReferralMaterial: (data?: CreatePromoMaterials) => Promise<void> = (
    data
  ) => {
    return this.apiClient.post(
      `clientarea/referral-materials/${data?.referralMaterialId}/promo-assets`,
      data
    )
  }
  updateReferralMaterial: (
    referralMaterialId: string,
    data: UpdateReferralMaterialDto
  ) => Promise<void> = (referralMaterialId, data) => {
    return this.apiClient.put(`clientarea/referral-materials/${referralMaterialId}`, data)
  }
  updateReferralMaterialCategory: (
    referralMaterialCategoryId: string,
    data: UpdateReferralMaterialCategoryDto
  ) => Promise<void> = (referralMaterialCategoryId, data) => {
    return this.apiClient.put(
      `clientarea/referral-materials/categories/${referralMaterialCategoryId}`,
      data
    )
  }
  deleteReferralMaterial: (referralMaterialId: string) => Promise<void> = (referralMaterialId) => {
    return this.apiClient.delete(`clientarea/referral-materials/${referralMaterialId}`)
  }
  deleteReferralMaterialCategory: (referralMaterialCategoryId: string) => Promise<void> = (
    referralMaterialCategoryId
  ) => {
    return this.apiClient.delete(
      `clientarea/referral-materials/categories/${referralMaterialCategoryId}`
    )
  }
  deletePromoMaterialsByPromoMaterialId: (
    referralMaterialId: string,
    promoMaterialId: string
  ) => Promise<void> = (referralMaterialId, promoMaterialId) => {
    return this.apiClient.delete(
      `clientarea/referral-materials/${referralMaterialId}/promo-assets/${promoMaterialId}`
    )
  }
  validatePromoMaterialsForReferralMaterial: (
    data?: CreatePromoMaterials
  ) => Promise<PreviewPromoMaterials> = (data) => {
    return this.apiClient.post(
      `clientarea/referral-materials/${data?.referralMaterialId}/promo-assets/validate`,
      data
    )
  }
  getUserDashboardsLayouts(
    userId: string,
    dashboardTypeIds?: number[]
  ): Promise<UserDashboardLayoutDto[]> {
    const params = new URLSearchParams()

    if (dashboardTypeIds?.length) {
      dashboardTypeIds.forEach((id) => params.append('dashboardTypeIds', String(id)))
    }

    const url =
      params.toString().length > 0
        ? `clientarea/dashboards/users/${userId}?${params.toString()}`
        : `clientarea/dashboards/users/${userId}`

    return this.apiClient.get(url)
  }
  getUserAvailableWidgets(userId: string, dashboardTypeIds?: number[]): Promise<UserWidgetDto[]> {
    const params = new URLSearchParams()

    params.append('userId', userId)

    if (dashboardTypeIds?.length) {
      dashboardTypeIds.forEach((id) => params.append('dashboardTypeIds', String(id)))
    }

    const url = params.toString()
      ? `clientarea/dashboards/users/${userId}/widgets?${params.toString()}`
      : `clientarea/dashboards/users/${userId}/widgets`

    return this.apiClient.get(url)
  }
  createUserDashboardLayout(
    userId: string,
    data: CreateUserDashboardLayoutRequest
  ): Promise<UserDashboardLayoutDto> {
    return this.apiClient.post(`clientarea/dashboards/users/${userId}/layouts`, data)
  }

  updateUserDashboardLayout(
    userId: string,
    layoutId: string,
    data: CreateUserDashboardLayoutRequest
  ): Promise<void> {
    return this.apiClient.put(`clientarea/dashboards/users/${userId}/layouts/${layoutId}`, data)
  }
  deleteUserDashboardLayout(userId: string, layoutId: string): Promise<void> {
    return this.apiClient.delete(`clientarea/dashboards/users/${userId}/layouts/${layoutId}`, {
      data: {
        userId,
        layoutId,
      },
    })
  }
}
